import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { getApiBase } from './Config';
import { handleAuthFailure } from './auth';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function TrialCouponsTab() {
  const [activeCodes, setActiveCodes] = useState([]);
  const [inactiveCodes, setInactiveCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    code: '',
    bonus_days: '',
    valid_from: '',
    valid_to: '',
    max_redemptions: '',
  });

  const authHeaders = () => ({
    Authorization: `Bearer ${Cookies.get('access_token')}`,
    'Content-Type': 'application/json',
  });

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = getApiBase();
      const product = 'trial_extension';
      const [activeRes, inactiveRes] = await Promise.all([
        fetch(`${base}/admin/promo-codes?status=active&product=${product}`, { headers: authHeaders() }),
        fetch(`${base}/admin/promo-codes?status=inactive&product=${product}`, { headers: authHeaders() }),
      ]);
      if (handleAuthFailure(activeRes) || handleAuthFailure(inactiveRes)) return;
      if (!activeRes.ok || !inactiveRes.ok) throw new Error('Failed to load trial coupons');
      setActiveCodes(await activeRes.json());
      setInactiveCodes(await inactiveRes.json());
    } catch (e) {
      setError(e.message || 'Failed to load trial coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        product: 'trial_extension',
        bonus_days: parseInt(form.bonus_days, 10),
        valid_from: form.valid_from,
        valid_to: form.valid_to,
        max_redemptions: form.max_redemptions.trim()
          ? parseInt(form.max_redemptions, 10)
          : null,
      };
      const res = await fetch(`${getApiBase()}/admin/promo-codes`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (handleAuthFailure(res)) return;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create coupon');
      }
      setShowModal(false);
      setForm({ code: '', bonus_days: '', valid_from: '', valid_to: '', max_redemptions: '' });
      await fetchCodes();
    } catch (e) {
      setError(e.message || 'Failed to create coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (id) => {
    if (!window.confirm('Disable this trial coupon?')) return;
    try {
      const res = await fetch(`${getApiBase()}/admin/promo-codes/${id}/disable`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to disable coupon');
      await fetchCodes();
    } catch (e) {
      setError(e.message || 'Failed to disable coupon');
    }
  };

  const renderTable = (codes, showDisable) => (
    <table className="min-w-full divide-y divide-gray-200 table-fixed">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Free days</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redemptions</th>
          {showDisable && (
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          )}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {codes.length === 0 ? (
          <tr>
            <td colSpan={showDisable ? 6 : 5} className="px-4 py-6 text-center text-gray-500 text-sm">
              No coupons in this section.
            </td>
          </tr>
        ) : (
          codes.map((c) => (
            <tr key={c.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.code}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{c.bonus_days ?? '—'}</td>
              <td className="px-4 py-3 text-sm text-gray-500 cell-wrap">{formatDate(c.valid_from)}</td>
              <td className="px-4 py-3 text-sm text-gray-500 cell-wrap">{formatDate(c.valid_to)}</td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {c.redemption_count}
                {c.max_redemptions != null ? ` / ${c.max_redemptions}` : ' / unlimited'}
              </td>
              {showDisable && (
                <td className="px-4 py-3 text-sm">
                  <button
                    type="button"
                    onClick={() => handleDisable(c.id)}
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Disable
                  </button>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="header-with-button">
        <div>
          <h2 className="text-xl font-semibold">Trial coupons</h2>
          <p className="text-sm text-gray-500 mt-1">
            Extra free days for app access. Users redeem in Settings. Works even after the default trial ends.
          </p>
        </div>
        <button type="button" className="add-button" onClick={() => setShowModal(true)}>
          Create trial coupon
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          <h3 className="text-lg font-medium mb-2 mt-4">Active coupons</h3>
          <div className="overflow-x-auto mb-8">{renderTable(activeCodes, true)}</div>

          <h3 className="text-lg font-medium mb-2">Inactive coupons</h3>
          <div className="overflow-x-auto">{renderTable(inactiveCodes, false)}</div>
        </>
      )}

      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => !submitting && setShowModal(false)}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Create trial coupon</h3>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Code</label>
                <input
                  type="text"
                  required
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm uppercase"
                  placeholder="FREE30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Bonus free days</label>
                <input
                  type="number"
                  required
                  min={1}
                  max={3650}
                  value={form.bonus_days}
                  onChange={(e) => setForm((f) => ({ ...f, bonus_days: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  placeholder="30"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start date</label>
                  <input
                    type="date"
                    required
                    value={form.valid_from}
                    onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End date</label>
                  <input
                    type="date"
                    required
                    value={form.valid_to}
                    min={form.valid_from || undefined}
                    onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max redemptions (optional)</label>
                <input
                  type="number"
                  min={1}
                  value={form.max_redemptions}
                  onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  placeholder="100"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium"
                >
                  {submitting ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
